import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MailOpen, Sparkles, Activity, Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const GOAL_LABEL = { weight: "Weight", strength: "Strength", sleep: "Sleep", energy: "Energy", consistency: "Showing up" };

export function EnvelopeCard({ env, onOpened }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try { const { data } = await api.post("/v2/envelope/open"); onOpened(data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (env.opened) return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="card p-5" data-testid="envelope-opened">
      <span className="eyebrow flex items-center gap-1.5"><MailOpen className="h-3.5 w-3.5" />{env.shape_label}</span>
      {env.step_text
        ? <><p className="text-xs text-stone-500 mt-2">What I planned for you:</p>
          <p className="font-display text-xl mt-1 leading-snug" data-testid="envelope-step">{env.step_text}</p></>
        : <><p className="font-display text-xl mt-2 leading-snug" data-testid="envelope-step">{env.line}</p>
          <p className="text-xs text-stone-500 mt-2" data-testid="envelope-surprise">Your step is chosen the moment you tell me how you feel. It's matched to this morning, not a guess from last night.</p></>}
      {env.step_text && <p className="text-xs text-stone-600 mt-3">First, tell me how you're feeling, so I can fit it to your day.</p>}
    </motion.div>
  );
  return (
    <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="card p-6 bg-[#2D2926] border-[#2D2926] text-stone-100" data-testid="envelope-sealed">
      <span className="eyebrow !text-stone-300 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />CAMRYN left you something · {env.shape_label}</span>
      <p className="font-display text-2xl mt-3 leading-snug" data-testid="envelope-teaser">{env.teaser}</p>
      <button onClick={open} disabled={busy} data-testid="envelope-open-btn" className="mt-5 px-6 h-11 rounded-full bg-white text-stone-900 text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2">
        <MailOpen className="h-4 w-4" />{busy ? "Opening…" : "Open it"}
      </button>
    </motion.div>
  );
}

export function SealedTomorrow({ env }) {
  if (!env) return null;
  return (
    <div className="card p-5 bg-[#F5F2EC]" data-testid="tomorrow-envelope">
      <span className="eyebrow flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Sealed until morning · {env.shape_label}</span>
      <p className="font-display text-lg mt-2 leading-snug" data-testid="tomorrow-envelope-teaser">{env.teaser}</p>
    </div>
  );
}

export function GoalPulse({ pulse, onChange }) {
  const [edit, setEdit] = useState(false);
  if (!pulse) return null;
  const pick = async (goal) => {
    try { await api.put("/v2/pulse/goal", { goal }); setEdit(false); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card p-4" data-testid="goal-pulse">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-full grid place-items-center bg-[#EEF2EC] text-[#4F6B55]"><Activity className="h-4 w-4" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[11px] uppercase tracking-wider font-semibold text-stone-500">{pulse.label}</span>
          <span className="block text-sm font-semibold text-stone-900" data-testid="goal-pulse-value">{pulse.value}</span>
          <span className="block text-xs text-stone-600" data-testid="goal-pulse-sub">{pulse.sub}</span>
        </span>
        <button onClick={() => setEdit(!edit)} data-testid="goal-pulse-change" className="tap text-xs underline text-stone-600">Change</button>
      </div>
      {edit && <div className="flex flex-wrap gap-1.5 mt-3" data-testid="goal-pulse-choices">
        {pulse.choices.map((g) => <button key={g} onClick={() => pick(g)} data-testid={`goal-pulse-${g}`}
          className={`chip px-3 py-2.5 rounded-full text-xs border ${pulse.goal === g ? "bg-stone-900 text-white border-stone-900" : "border-stone-200"}`}>{GOAL_LABEL[g]}</button>)}
      </div>}
    </div>
  );
}

export function RevealCard({ reveal }) {
  if (!reveal) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
      className="card p-6 bg-gradient-to-br from-[#FFF8EC] to-white" data-testid="reveal-card">
      <span className="eyebrow flex items-center gap-1.5 !text-[#8A5A16]"><Eye className="h-3.5 w-3.5" />{reveal.title}</span>
      <p className="font-display text-xl mt-2 leading-snug" data-testid="reveal-text">{reveal.text}</p>
    </motion.div>
  );
}

export const extraLabel = (d) => d.extra?.kind === "challenge" ? "Today only · ends tonight"
  : d.extra?.kind === "rescue" ? "Your 60-second win" : d.shape === "experiment" && d.extra?.kind === "discovery" ? "Today's experiment" : null;

export const ShapeIcon = Sparkles;
